import { Box, Image, InlineStack, Text, Tooltip } from '@customerio/pluma-components/react';
import { Icon } from '@customerio/pluma-components/react';
import type { Source } from '@customerio/hydra-ui-api';
import { getSourceIconInfo, groupSourcesByType } from '../utils/sources';

/** How many source marks a cell shows before collapsing the rest into a count. */
const MAX_DISPLAY_SOURCES = 3;

export type SourcesCellProps = {
  sources: readonly Source[];
};

/**
 * Where a row's data came from.
 *
 * Repeat writes from the same place collapse into one mark, so a row written
 * a thousand times by one campaign does not read as a thousand sources.
 */
export function SourcesCell({ sources }: SourcesCellProps) {
  const groups = groupSourcesByType([...sources]);

  if (groups.length === 0) {
    return <Text color="placeholder">-</Text>;
  }

  const shown = groups.slice(0, MAX_DISPLAY_SOURCES);
  const remaining = groups.slice(MAX_DISPLAY_SOURCES).flat();

  return (
    <InlineStack alignBlock="center" gap="050">
      {shown.map((group) => {
        const [source] = group;
        return source == null ? null : <SourceMark key={source.type} source={source} count={group.length} />;
      })}
      {remaining.length > 0 ? (
        <Tooltip content={remaining.map((source) => getSourceIconInfo(source).title).join(', ')}>
          <Text color="placeholder" size="xs">
            +{remaining.length}
          </Text>
        </Tooltip>
      ) : null}
    </InlineStack>
  );
}

type SourceMarkProps = {
  source: Source;
  count: number;
};

function SourceMark({ source, count }: SourceMarkProps) {
  const { icon, imageSrc, title } = getSourceIconInfo(source);
  const label = count > 1 ? `Multiple sources (${title} and others)` : title;

  return (
    <Tooltip content={label}>
      <Box display="inline-flex">
        {imageSrc != null ? (
          <Image src={imageSrc} alt={label} width="16" height="16" />
        ) : (
          <Icon name={icon ?? 'import'} size="sm" color="subtle" flexShrink="0" />
        )}
      </Box>
    </Tooltip>
  );
}
